import type { components } from '../../api/generated/schema';
import type { SignupRequest } from '../../domain/account';
import type {
  AcceptResult,
  CreatedInvitation,
  GroupLinkLimits,
  Invitation,
  InvitationLink,
  InvitationStatus,
  InvitePreview,
  ReceivedInvitation,
} from '../../domain/invitations';
import { toCollectionRef, toPersonRef } from '../mapping';

type S = components['schemas'];

const STATUS: Record<S['InvitationStatusEnum'], InvitationStatus> = {
  usable: 'usable',
  revoked: 'revoked',
  expired: 'expired',
  used_up: 'usedUp',
};

/**
 * Anything the domain doesn't know reads as expired: the web has no
 * "unknown", and an inviter must never be shown a decline.
 */
export function toInvitationStatus(status: string): InvitationStatus {
  return STATUS[status as S['InvitationStatusEnum']] ?? 'expired';
}

export function toInvitation(i: S['Invitation']): Invitation {
  return {
    id: i.id,
    kind: i.kind,
    label: i.label,
    invitee: i.invitee ? toPersonRef(i.invitee) : null,
    offers: i.offers.map((o) => ({
      id: o.id,
      collection: o.collection ? toCollectionRef(o.collection) : null,
      role: o.role,
    })),
    maxUses: i.max_uses,
    useCount: i.use_count,
    expiresAt: i.expires_at,
    status: toInvitationStatus(i.status),
    acceptedBy: i.accepted_by.map(toPersonRef),
    createdAt: i.created_at,
  };
}

export function toCreatedInvitation(created: S['InvitationCreated']): CreatedInvitation {
  const { link, ...invitation } = created;
  return { invitation: toInvitation(invitation), link };
}

export function toInvitationLink(l: S['InvitationLinkCreated']): InvitationLink {
  return { id: l.id, link: l.link, createdAt: l.created_at };
}

export function toInvitePreview(p: S['InvitePreview']): InvitePreview {
  return {
    signedIn: p.signed_in,
    inviter:
      'last_initial' in p.inviter
        ? {
            kind: 'initialOnly',
            firstName: p.inviter.first_name,
            lastInitial: p.inviter.last_initial,
          }
        : { kind: 'person', person: toPersonRef(p.inviter) },
    connects: p.connects,
    nothingNew: p.nothing_new,
    canDecline: p.can_decline,
    offers: p.offers.map((o) => ({
      offerId: o.offer_id,
      collectionName: o.collection_name,
      status: o.status === 'already_following' ? 'alreadyFollowing' : 'available',
      checked: o.checked,
    })),
  };
}

export function toReceivedInvitation(r: S['ReceivedInvitation']): ReceivedInvitation {
  const { invitation_id, ...preview } = r;
  return { ...toInvitePreview(preview), invitationId: invitation_id };
}

type LimitsBody = Pick<S['InvitationCreateRequest'], 'max_uses' | 'expires_at'>;

/** A default limit leaves its field out (the server's 20 / 30 days); no limit is an explicit null. */
export function toGroupLinkBody(limits: GroupLinkLimits): LimitsBody {
  const body: LimitsBody = {};
  if (limits.maxUses.kind === 'unlimited') body.max_uses = null;
  if (limits.maxUses.kind === 'count') body.max_uses = limits.maxUses.count;
  if (limits.expiry.kind === 'never') body.expires_at = null;
  if (limits.expiry.kind === 'at') body.expires_at = limits.expiry.at;
  return body;
}

export function toAcceptResult(r: S['InviteAcceptResult']): AcceptResult {
  return {
    inviter: toPersonRef(r.inviter),
    connected: r.connected,
    joined: r.joined.map(toCollectionRef),
  };
}

export function toInviteSignup(token: string, r: SignupRequest): S['InviteSignupRequest'] {
  return {
    token,
    first_name: r.firstName,
    last_name: r.lastName,
    username: r.username,
    email: r.email,
    phone: r.phone,
    password: r.password,
    accepted_terms: true,
    terms_version: r.termsVersion,
  };
}
